import {
    getLeafGroupLabel,
    groupAccountTypesByClass,
    orderedPredefinedLeafTypes,
} from "@common/constants/domains/accounting/accountGroups";
import {
    AccountClass,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import {
    getAccountClassForType,
    isAccountType,
} from "@common/constants/domains/accounting/helpers";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { compareScopeOrder } from "./chartScopeOrdering";
import { GroupNameChainNode, resolveGroupChain } from "./accountMatchesFilters";
import { mergeGroupsForDisplay, MergedGroup } from "./entityGroupMerge";

export interface CoaGroupFilterOption {
    id: string;
    label: string;
    children: CoaGroupFilterOption[];
}

export interface CoaGroupFilterGroup extends GroupNameChainNode {
    type: AccountType;
    sortOrder: number;
}

export interface CoaGroupFilterAccount {
    type: AccountType;
    customGroupChainIds: string[];
}

const CUSTOM_GROUP_KEY_PREFIX = "group:";

export const classGroupKey = (accountClass: AccountClass): string =>
    `class:${accountClass}`;
export const leafGroupKey = (type: AccountType): string => `leaf:${type}`;
export const customGroupKey = (groupId: string): string =>
    `${CUSTOM_GROUP_KEY_PREFIX}${groupId}`;
export const mergedCustomGroupKey = (groupIds: string[]): string =>
    customGroupKey([...groupIds].sort().join(","));

const selectedCustomGroupIds = (groupKeySet: Set<string>): Set<string> => {
    const ids = new Set<string>();
    for (const key of groupKeySet) {
        if (!key.startsWith(CUSTOM_GROUP_KEY_PREFIX)) {
            continue;
        }
        for (const id of key.slice(CUSTOM_GROUP_KEY_PREFIX.length).split(",")) {
            if (id.length > 0) {
                ids.add(id);
            }
        }
    }
    return ids;
};

const compareFilterGroups = (
    first: CoaGroupFilterGroup,
    second: CoaGroupFilterGroup,
): number =>
    compareScopeOrder(
        {
            id: first.id,
            kind: "group",
            name: first.name,
            sortOrder: first.sortOrder,
        },
        {
            id: second.id,
            kind: "group",
            name: second.name,
            sortOrder: second.sortOrder,
        },
    );

const compareMergedFilterGroups = (
    first: MergedGroup,
    second: MergedGroup,
): number =>
    compareFilterGroups(
        {
            id: first.group.id,
            name: first.group.name,
            parentGroupId: first.group.parentGroupId,
            type: first.group.type,
            sortOrder: first.mergedSortOrder,
        },
        {
            id: second.group.id,
            name: second.group.name,
            parentGroupId: second.group.parentGroupId,
            type: second.group.type,
            sortOrder: second.mergedSortOrder,
        },
    );

const buildClassLeafFilterOptions = (
    accountTypes: AccountType[],
    childrenForType: (type: AccountType) => CoaGroupFilterOption[],
): CoaGroupFilterOption[] => {
    const presentLeafTypes = new Set(accountTypes);
    const orderedPresentLeafTypes = orderedPredefinedLeafTypes.filter((type) =>
        presentLeafTypes.has(type),
    );
    const remainingLeafTypes = [...presentLeafTypes].filter(
        (type) => !orderedPresentLeafTypes.includes(type),
    );
    const orderedTypes = [...orderedPresentLeafTypes, ...remainingLeafTypes];
    const typeClassGroups = groupAccountTypesByClass(orderedTypes);

    return typeClassGroups.map(({ accountClass, types }) => ({
        id: classGroupKey(accountClass),
        label: accountClass,
        children: types.map((type) => ({
            id: leafGroupKey(type),
            label: getLeafGroupLabel(type),
            children: childrenForType(type),
        })),
    }));
};

export const accountMatchesCoaGroupKeys = ({
    accountType,
    customGroupChainIds,
    groupKeys,
}: {
    accountType: string | null | undefined;
    customGroupChainIds: string[];
    groupKeys?: string[];
}): boolean => {
    if (!groupKeys || groupKeys.length === 0) {
        return true;
    }
    const groupKeySet = new Set(groupKeys);
    if (accountType && isAccountType(accountType)) {
        if (
            groupKeySet.has(classGroupKey(getAccountClassForType(accountType)))
        ) {
            return true;
        }
        if (groupKeySet.has(leafGroupKey(accountType))) {
            return true;
        }
    }
    const selectedGroupIds = selectedCustomGroupIds(groupKeySet);
    return customGroupChainIds.some((id) => selectedGroupIds.has(id));
};

export const buildCoaGroupFilterOptions = ({
    accounts,
    groups,
}: {
    accounts: CoaGroupFilterAccount[];
    groups: CoaGroupFilterGroup[];
}): CoaGroupFilterOption[] => {
    const referencedGroupIds = new Set<string>();
    accounts.forEach((account) =>
        account.customGroupChainIds.forEach((id) => referencedGroupIds.add(id)),
    );
    const referencedGroups = groups.filter((group) =>
        referencedGroupIds.has(group.id),
    );
    const referencedGroupsById = new Map(
        referencedGroups.map((group) => [group.id, group]),
    );
    const buildGroupOption = (
        group: CoaGroupFilterGroup,
    ): CoaGroupFilterOption => ({
        id: customGroupKey(group.id),
        label: group.name,
        children: referencedGroups
            .filter(
                (childGroup) =>
                    childGroup.parentGroupId === group.id &&
                    childGroup.type === group.type,
            )
            .sort(compareFilterGroups)
            .map(buildGroupOption),
    });

    return buildClassLeafFilterOptions(
        accounts.map((account) => account.type),
        (type) =>
            referencedGroups
                .filter((group) => {
                    if (group.type !== type) {
                        return false;
                    }
                    if (group.parentGroupId === null) {
                        return true;
                    }
                    const parentGroup = referencedGroupsById.get(
                        group.parentGroupId,
                    );
                    return parentGroup?.type !== type;
                })
                .sort(compareFilterGroups)
                .map(buildGroupOption),
    );
};

export const buildCoaGroupFilterOptionsFromChartAccounts = ({
    accounts,
    groups,
    displayedEntityIds,
}: {
    accounts: Array<{
        type: AccountType;
        entityId: number;
        parentGroupId: string | null;
    }>;
    groups: AccountGroup[];
    displayedEntityIds?: number[];
}): CoaGroupFilterOption[] => {
    const scopedAccounts =
        displayedEntityIds === undefined
            ? accounts
            : accounts.filter((account) =>
                  displayedEntityIds.includes(account.entityId),
              );
    const { mergedGroupsByParentKey, groupKeyById, groupsById } =
        mergeGroupsForDisplay({
            groups,
            selectedEntityIds: displayedEntityIds,
        });

    const referencedMergedKeys = new Set<string>();
    scopedAccounts.forEach((account) => {
        resolveGroupChain(account.parentGroupId, groupsById).forEach(
            (group) => {
                const mergedKey = groupKeyById.get(group.id);
                if (mergedKey) {
                    referencedMergedKeys.add(mergedKey);
                }
            },
        );
    });

    const mergedByKey = new Map<string, MergedGroup>();
    for (const siblings of mergedGroupsByParentKey.values()) {
        siblings.forEach((merged) => {
            mergedByKey.set(merged.key, merged);
        });
    }

    const buildMergedOption = (
        merged: MergedGroup,
        visitedKeys: ReadonlySet<string>,
    ): CoaGroupFilterOption => {
        const nextVisitedKeys = new Set(visitedKeys);
        nextVisitedKeys.add(merged.key);
        return {
            id: mergedCustomGroupKey(merged.groups.map((group) => group.id)),
            label: merged.group.name,
            children: (mergedGroupsByParentKey.get(merged.key) ?? [])
                .filter(
                    (child) =>
                        referencedMergedKeys.has(child.key) &&
                        !nextVisitedKeys.has(child.key),
                )
                .sort(compareMergedFilterGroups)
                .map((child) => buildMergedOption(child, nextVisitedKeys)),
        };
    };

    return buildClassLeafFilterOptions(
        scopedAccounts.map((account) => account.type),
        (type) =>
            [...mergedByKey.values()]
                .filter((merged) => {
                    if (!referencedMergedKeys.has(merged.key)) {
                        return false;
                    }
                    if (merged.group.type !== type) {
                        return false;
                    }
                    if (merged.parentKey === null) {
                        return true;
                    }
                    const parent = mergedByKey.get(merged.parentKey);
                    return parent?.group.type !== type;
                })
                .sort(compareMergedFilterGroups)
                .map((merged) => buildMergedOption(merged, new Set())),
    );
};
